import { useEffect, useState } from "react";
import { ScrollToTop } from "../ScrollToTop";
import { Chen, CHEN_CSS } from "./Chen";
import { Consola, download, EXERCICIS, script, Sql, buildDb, loadSql } from "./Consola";
import { Panell, int } from "./Grafics";
import { CRU, DADES, TAULES, ddl } from "./model";

/**
 * /dades — pàgina interna (sense enllaços, noindex, fora del sitemap) per a l'assignatura
 * d'Emmagatzematge de dades. Fa amb les dades de la web el mateix que els exercicis resolts dels
 * apunts: enunciat → entitats → relacions (Chen) → model lògic → model físic, i després
 * normalització, càrrega, un panell amb gràfics, exercicis de consultes i una consola SQL.
 */

const CSS = `
.dades-page {
  --s1: #2a78d6; --s2: #eb6834; --s3: #1baf7a;
  --up: #e34948; --down: #2a78d6;
  --acc: #2a78d6; --warn: #c98500;
  --grid: rgba(0,0,0,.08);
}
.dark .dades-page {
  --s1: #3987e5; --s2: #d95926; --s3: #199e70;
  --up: #e66767; --down: #3987e5;
  --acc: #5b9cf0; --warn: #e0a520;
  --grid: rgba(255,255,255,.1);
}
.dades-page .tk-k { color: var(--acc); font-weight: 600; }
.dades-page .tk-s { color: #1a8a5f; }
.dark .dades-page .tk-s { color: #4cc596; }
.dades-page .tk-n { color: #b4570f; }
.dark .dades-page .tk-n { color: #f0a050; }
.dades-page .tk-c { color: var(--muted-foreground); font-style: italic; }
.dades-page details > summary { list-style: none; cursor: pointer; }
.dades-page details > summary::-webkit-details-marker { display: none; }
${CHEN_CSS}
`;

const PASSOS = [
  ["enunciat", "Enunciat"],
  ["entitats", "1 · Entitats"],
  ["relacions", "2 · Relacions"],
  ["logic", "3 · Model lògic"],
  ["fisic", "4 · Model físic"],
  ["normalitzacio", "5 · Normalització"],
  ["carrega", "6 · Càrrega"],
  ["local", "7 · A Local"],
  ["dades", "Les dades"],
  ["exercicis", "Consultes"],
  ["consola", "Consola SQL"],
] as const;

function Seccio({ id, pas, titol, children, intro }: { id: string; pas?: string; titol: string; intro?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      {pas && <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--acc)" }}>{pas}</p>}
      <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-1">{titol}</h2>
      {intro && <div className="mt-3 max-w-3xl text-muted-foreground leading-relaxed text-pretty space-y-3">{intro}</div>}
      <div className="mt-6">{children}</div>
    </section>
  );
}

/** Caixa «per què» com les dels apunts. */
function Clau({ titol, children }: { titol: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/40 px-4 py-3 text-sm leading-relaxed text-foreground">
      <p className="font-semibold mb-1">{titol}</p>
      <div className="text-muted-foreground space-y-1.5">{children}</div>
    </div>
  );
}

function Badge({ k }: { k: string }) {
  const pk = k.includes("PK"), fk = k.includes("FK");
  const style = pk && fk
    ? { background: "linear-gradient(90deg, color-mix(in oklab, var(--warn) 22%, transparent), color-mix(in oklab, var(--acc) 18%, transparent))" }
    : pk
      ? { background: "color-mix(in oklab, var(--warn) 22%, transparent)", color: "color-mix(in oklab, var(--warn) 75%, var(--foreground))" }
      : { background: "color-mix(in oklab, var(--acc) 15%, transparent)", color: "var(--acc)" };
  return <i className="not-italic shrink-0 rounded-md px-1.5 py-px font-mono text-[10px] font-bold" style={style}>{k}</i>;
}

// ─── Passos ───────────────────────────────────────────────────────────────────

const ENTITATS: [string, string, string, string][] = [
  ["MARCA", "MarcaID", "Nombre, Descripcion", "Té vida pròpia: existeix encara que no tingui cap model a la llista."],
  ["MODELO", "ModeloID", "Nombre, Potencia, Longitud, Anchura, Altura, Maletero", "El centre de tot. Una fila per versió de combustible, com fa la revista."],
  ["COMBUSTIBLE", "CombustibleID", "Nombre, Grupo", "Es repeteix a centenars de models i té un atribut propi (el grup)."],
  ["EDICION", "EdicionID", "Revista, Fecha, Paginas", "Cada número de la revista. Sense aquesta entitat no podríem guardar més d'un preu per model."],
  ["MATRICULACION", "MatriculacionID", "Fecha, Matricula, Modelo", "Un fet: un cotxe vist un dia concret."],
  ["DESCATALOGADO", "DescatalogadoID", "Nombre, Combustible, Generaciones, AnioInicio, AnioFin", "Models que ja no es fan. Tenen atributs diferents dels de MODELO (anys i generacions, sense preu ni mides)."],
];

const RELACIONS: [string, string][] = [
  ["MARCA 1 ── fabrica ── N MODELO", "Una marca fa molts models; cada model és d'una sola marca."],
  ["MODELO N ── usa ── 1 COMBUSTIBLE", "Cada model (versió) té un combustible; un combustible el fan servir molts models."],
  ["MODELO N ── aparece ── M EDICION", "Un model surt a moltes edicions i cada edició porta molts models. El Precio no és ni del model ni de l'edició: és de la parella. És un atribut de la relació."],
  ["MARCA 1 ── registra ── N MATRICULACION", "Cada matrícula és d'una marca; una marca en té moltes. La relació és opcional: algunes matrícules són de marques que no tenim (motos, camions)."],
  ["MARCA 1 ── tuvo ── N DESCATALOGADO", "Cada model descatalogat és d'una marca."],
];

const CHEN = [
  "MARCA: MarcaID, Nombre, Descripcion | 1 | fabrica | N | MODELO: ModeloID, Nombre, Potencia, Longitud, Anchura, Altura, Maletero | N | usa | 1 | COMBUSTIBLE: CombustibleID, Nombre, Grupo",
  "MODELO: ModeloID, Nombre | N | aparece + Precio | M | EDICION: EdicionID, Revista, Fecha, Paginas",
  "MARCA: MarcaID, Nombre | 1 | registra | N | MATRICULACION: MatriculacionID, Fecha, Matricula, Modelo",
  "MARCA: MarcaID, Nombre | 1 | tuvo | N | DESCATALOGADO: DescatalogadoID, Nombre, Combustible, Generaciones, AnioInicio, AnioFin",
];

function Logic() {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 items-start">
      {TAULES.map((t) => (
        <div key={t.nom} className={`flex flex-col min-w-0 rounded-xl border bg-background overflow-hidden text-[13px] ${t.pont ? "border-dashed" : "border-border"}`} style={t.pont ? { borderColor: "var(--acc)" } : undefined}>
          <b className="px-3 py-2 bg-secondary border-b border-border text-xs tracking-wide text-foreground">
            {t.nom.toUpperCase()}
            {t.pont && <span className="ml-1.5 font-normal text-muted-foreground">· taula pont</span>}
          </b>
          {t.cols.map((c) => (
            <span key={c.nom} className="flex flex-wrap items-center justify-between gap-x-1.5 px-3 py-1.5 border-t border-border first-of-type:border-t-0">
              <em className="not-italic min-w-0 text-foreground">{c.nom}</em>
              {(c.pk || c.fk) && <Badge k={[c.pk && "PK", c.fk && "FK"].filter(Boolean).join(", ")} />}
              {c.fk && <small className="basis-full font-mono text-[11px] font-semibold text-muted-foreground">→ {c.fk.toUpperCase()}</small>}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function Fisic() {
  const [t, setT] = useState(TAULES[0]!.nom);
  const taula = TAULES.find((x) => x.nom === t)!;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {TAULES.map((x) => (
          <button key={x.nom} type="button" aria-pressed={x.nom === t} onClick={() => setT(x.nom)} className={`h-8 px-3 rounded-full border font-mono text-xs transition-colors ${x.nom === t ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {x.nom}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr>
              {["Camp", "Tipus", "Restricció", "Per què"].map((h) => <th key={h} className="px-3 py-2 text-xs font-semibold text-foreground whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {taula.cols.map((c) => (
              <tr key={c.nom} className="border-t border-border align-top">
                <td className="px-3 py-2 font-mono text-xs text-foreground whitespace-nowrap">{c.nom}</td>
                <td className="px-3 py-2 font-mono text-xs whitespace-nowrap" style={{ color: "var(--acc)" }}>{c.tipus}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground min-w-[9rem]">
                  {[c.pk && "PRIMARY KEY", c.fk && `FOREIGN KEY → ${c.fk}`, c.extra, c.nul ? "NULL" : !c.pk && !c.extra?.includes("NOT NULL") && "NOT NULL"].filter(Boolean).join(" · ")}
                </td>
                <td className="px-3 py-2 text-muted-foreground min-w-[14rem] text-pretty">{c.why}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Abans → després, amb exemples de les dades de debò. */
function AbansDespres({ abans, despres }: { abans: [string, string][]; despres: string[][] }) {
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_auto_1fr] items-center text-xs">
      <table className="w-full rounded-lg border border-border overflow-hidden font-mono">
        <tbody>
          {abans.map(([a, b], i) => (
            <tr key={i} className="border-t border-border first:border-t-0">
              <td className="px-2.5 py-1.5 text-muted-foreground">{a}</td>
              <td className="px-2.5 py-1.5 text-red-600 dark:text-red-400">{b}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <span className="text-center text-muted-foreground rotate-90 sm:rotate-0" aria-hidden="true">→</span>
      <table className="w-full rounded-lg border border-border overflow-hidden font-mono">
        <tbody>
          {despres.map((r, i) => (
            <tr key={i} className={`border-t border-border first:border-t-0 ${i === 0 ? "bg-secondary font-semibold text-foreground" : "text-foreground"}`}>
              {r.map((c, j) => <td key={j} className="px-2.5 py-1.5">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Normalitzacio() {
  const d = DADES.Descatalogados.find((x) => x.AnioInicio && x.AnioFin)!;
  const m = CRU.modelos.find((x) => x.potencia && x.preu_juliol)!;
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border p-5 space-y-4">
        <h3 className="font-semibold text-foreground">1FN · Valors atòmics</h3>
        <p className="text-sm text-muted-foreground text-pretty">Cada cel·la ha de tenir un sol valor, i del tipus que toca. La revista porta textos que en barregen diversos:</p>
        <AbansDespres
          abans={[["Anys", `"${d.AnioInicio}–${d.AnioFin}"`], ["Preu", `"${new Intl.NumberFormat("ca-ES").format(m.preu_juliol!)}€"`], ["Potència", `"100 / 140"`]]}
          despres={[["AnioInicio", "AnioFin", "Precio", "Potencia"], [String(d.AnioInicio), String(d.AnioFin), `${m.preu_juliol}.00`, "100"]]}
        />
        <p className="text-sm text-muted-foreground text-pretty">Separats i com a números, ja es pot restar (quants anys es va fabricar), ordenar per preu o fer mitjanes. Quan la potència té diverses versions («100 / 140»), es guarda la primera.</p>
      </div>
      <div className="rounded-2xl border border-border p-5 space-y-3">
        <h3 className="font-semibold text-foreground">2FN · Tot depèn de la clau sencera</h3>
        <p className="text-sm text-muted-foreground text-pretty">
          Només afecta les taules amb clau composta: <b className="font-mono text-foreground">Precios</b> (ModeloID + EdicionID). <b className="text-foreground">Precio</b> depèn de les dues coses alhora (el mateix model val diferent a cada edició), així que compleix. Si hi poséssim el <b className="text-foreground">Nombre</b> del model, dependria només de ModeloID i es repetiria a cada edició: per això el nom és a Modelos.
        </p>
      </div>
      <div className="rounded-2xl border border-border p-5 space-y-3">
        <h3 className="font-semibold text-foreground">3FN · Res depèn d'un camp que no és la clau</h3>
        <p className="text-sm text-muted-foreground text-pretty">
          Si Modelos guardés el combustible i el grup, <b className="text-foreground">Grupo</b> dependria de <b className="text-foreground">Combustible</b> i no del model: «PHEV → Electrificat» es repetiria {int(DADES.Modelos.filter((x) => DADES.Combustibles.find((c) => c.CombustibleID === x.CombustibleID)!.Grupo === "Electrificat").length)} vegades. Per això hi ha la taula <b className="font-mono text-foreground">Combustibles</b>: el grup s'escriu un sol cop.
        </p>
        <p className="text-sm text-muted-foreground text-pretty">
          Pel mateix motiu no es guarda l'<b className="text-foreground">estat</b> que porta la revista («Nou», «Actualitzat»): és una dada derivada. Es calcula comparant els dos preus (vegeu la consulta 9). Si es guardés, podria contradir els preus.
        </p>
      </div>
    </div>
  );
}

function Carrega() {
  const senseMarca = DADES.Matriculaciones.filter((m) => !m.MarcaID).length;
  const senseAnys = DADES.Descatalogados.filter((m) => !m.AnioInicio).length;
  const sensePot = DADES.Modelos.filter((m) => !m.Potencia).length;
  return (
    <div className="space-y-6">
      <ol className="grid gap-2 sm:grid-cols-4 text-sm">
        {[
          ["Extracció", "Revista Coches TV (PDF, març i juliol de 2026) i matrícules vistes al carrer."],
          ["Transformació", "PDF → CSV: una fila per model i combustible; mides, potència i preus a número; dates a AAAA-MM-DD."],
          ["Càrrega", "Es reparteix en set taules, les marques s'identifiquen pel nom («SKODA» = «Škoda») i es comprova cada FK."],
          ["Consulta", "SQL. Aquí mateix, amb SQLite al navegador."],
        ].map(([t, d], i) => (
          <li key={t} className="rounded-xl border border-border p-3">
            <p className="text-xs font-mono" style={{ color: "var(--acc)" }}>{i + 1}</p>
            <p className="font-medium text-foreground">{t}</p>
            <p className="text-xs text-muted-foreground mt-1 text-pretty">{d}</p>
          </li>
        ))}
      </ol>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold">Taula</th>
                <th className="px-3 py-2 text-xs font-semibold text-right">Files</th>
              </tr>
            </thead>
            <tbody>
              {TAULES.map((t) => (
                <tr key={t.nom} className="border-t border-border">
                  <td className="px-3 py-1.5 font-mono text-xs text-foreground">{t.nom}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-foreground">{int((DADES[t.nom as keyof typeof DADES] as unknown[]).length)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Clau titol="Problemes que surten en carregar">
          <p><b className="text-foreground">{senseMarca} matriculacions</b> són de marques que no tenim (motos i camions): MarcaID queda a NULL. Per això aquesta FK accepta NULL.</p>
          <p><b className="text-foreground">{senseAnys} descatalogats</b> no tenen anys llegibles («n.d.»): AnioInicio i AnioFin queden a NULL.</p>
          <p><b className="text-foreground">{sensePot} models</b> no porten potència a la revista.</p>
          <p>La revista separa <b className="text-foreground">Defender, Discovery i Range Rover</b> com si fossin marques; aquí són models de Land Rover.</p>
        </Clau>
      </div>
    </div>
  );
}

type Resultat = { columns: string[]; values: unknown[][] };

/** Les primeres files del resultat d'una consulta resolta. */
function MiniTaula({ r }: { r: Resultat }) {
  const MAX = 5;
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1.5">
        Resultat: {r.values.length} {r.values.length === 1 ? "fila" : "files"}
        {r.values.length > MAX && ` (les ${MAX} primeres)`}
      </p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead className="bg-secondary">
            <tr>{r.columns.map((c) => <th key={c} className="px-2.5 py-1.5 text-left font-mono font-semibold whitespace-nowrap">{c}</th>)}</tr>
          </thead>
          <tbody>
            {r.values.slice(0, MAX).map((row, i) => (
              <tr key={i} className="border-t border-border">
                {row.map((v, j) => (
                  <td key={j} className={`px-2.5 py-1 whitespace-nowrap ${typeof v === "number" ? "text-right tabular-nums" : ""} ${v == null ? "text-muted-foreground" : "text-foreground"}`}>
                    {v == null ? "NULL" : String(v)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Comprova que hi ha arribat tot: el nombre de files de cada taula, en una sola consulta. */
const COMPROVA = TAULES.map((t) => `SELECT '${t.nom}' AS Tabla, COUNT(*) AS Filas FROM ${t.nom}`).join("\nUNION ALL ") + ";";

function Local() {
  const passos: [string, React.ReactNode][] = [
    ["Crea la web", <>A Local, <b className="text-foreground">+ → Create a new site</b>, posa-li un nom (per exemple «cotxes») i deixa l'entorn <b className="text-foreground">Preferred</b>. Local hi instal·la WordPress amb la seva base de dades MySQL.</>],
    ["Obre la base de dades", <>Amb la web engegada (<b className="text-foreground">Start site</b>), ves a la pestanya <b className="text-foreground">Database</b> i toca <b className="text-foreground">Open Adminer</b>. Veuràs les taules de WordPress (wp_posts, wp_users…).</>],
    ["Importa l'script", <>Baixa l'<b className="text-foreground">script per a Local</b> d'aquí sota. A Adminer, <b className="text-foreground">Import → Choose files</b>, tria'l i toca <b className="text-foreground">Execute</b>. Crea les set taules i hi posa totes les files; si el tornes a importar, primer les esborra.</>],
    ["Comprova-ho", <>A Adminer, <b className="text-foreground">SQL command</b> i enganxa la consulta de sota. Has d'obtenir els mateixos números que la taula del pas 6.</>],
  ];
  return (
    <div className="space-y-6">
      <ol className="grid gap-3 sm:grid-cols-2">
        {passos.map(([t, d], i) => (
          <li key={t} className="rounded-xl border border-border p-4">
            <p className="text-xs font-mono" style={{ color: "var(--acc)" }}>{i + 1}</p>
            <p className="font-medium text-foreground">{t}</p>
            <p className="text-sm text-muted-foreground mt-1 text-pretty">{d}</p>
          </li>
        ))}
      </ol>
      <Baixades />
      <div className="space-y-2">
        <h3 className="font-semibold text-foreground">Consulta per comprovar-ho</h3>
        <Sql code={COMPROVA} />
      </div>
      <Clau titol="Les taules del treball i les de WordPress, juntes">
        <p>Totes viuen a la mateixa base de dades (<span className="font-mono text-foreground">local</span>). Les de WordPress comencen per <span className="font-mono text-foreground">wp_</span> i les del treball no, així que no es barregen. Per veure-les juntes a Adminer, mira la llista de l'esquerra.</p>
        <p>Local fa servir MySQL 8. L'script també funciona a MariaDB: els tipus, les FK i els CHECK són els mateixos.</p>
      </Clau>
    </div>
  );
}

function Exercicis({ prova }: { prova: (sql: string) => void }) {
  const [res, setRes] = useState<(Resultat | null)[]>([]);
  useEffect(() => {
    loadSql()
      .then((SQL) => {
        const db = buildDb(SQL);
        setRes(EXERCICIS.map((e) => db.exec(e.sql)[0] ?? null));
      })
      .catch(() => {});
  }, []);
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {EXERCICIS.map((e, i) => (
        <article key={e.titol} className="rounded-2xl border border-border p-4 sm:p-5 min-w-0">
          <p className="text-xs font-mono text-muted-foreground">
            Consulta {i + 1} · <span style={{ color: "var(--acc)" }}>{e.tema}</span>
          </p>
          <h3 className="font-semibold text-foreground mt-1">{e.titol}</h3>
          <p className="text-sm text-muted-foreground mt-1.5 italic text-pretty">{e.enunciat}</p>
          <div className="mt-3 space-y-3">
            <Sql code={e.sql} />
            {res[i] ? <MiniTaula r={res[i]!} /> : <p className="text-xs text-muted-foreground">Calculant el resultat…</p>}
            <p className="text-sm text-muted-foreground text-pretty">{e.nota}</p>
            <button type="button" onClick={() => prova(e.sql)} className="h-8 px-3 rounded-full border border-border text-xs font-medium text-foreground hover:bg-secondary">
              Obre-la a la consola ↓
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

function Baixades() {
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-wrap gap-2">
      <button type="button" onClick={() => download("cotxes_local.sql", script(), "application/sql")} className="h-9 px-4 rounded-full bg-foreground text-background text-sm font-medium">
        Script SQL per a Local
      </button>
      <button type="button" onClick={() => download("cotxes_create.sql", ddl("mysql") + "\n", "application/sql")} className="h-9 px-4 rounded-full border border-border text-sm text-foreground">
        Només CREATE TABLE
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          loadSql()
            .then((SQL) => download("cotxes.sqlite", buildDb(SQL).export() as BlobPart, "application/vnd.sqlite3"))
            .finally(() => setBusy(false));
        }}
        className="h-9 px-4 rounded-full border border-border text-sm text-foreground disabled:opacity-50"
      >
        Base de dades (.sqlite)
      </button>
    </div>
  );
}

// ─── Pàgina ───────────────────────────────────────────────────────────────────

export const Dades = () => {
  const [query, setQuery] = useState({ sql: EXERCICIS[0]!.sql, n: 0 });

  // No indexada: ni a Google ni al sitemap (vercel.json també hi posa X-Robots-Tag).
  useEffect(() => {
    document.title = "Dades | Cotx.es";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const prova = (sql: string) => {
    setQuery((q) => ({ sql, n: q.n + 1 }));
    document.getElementById("consola")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="dades-page min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-10 py-12 max-w-6xl">
          <header>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pàgina interna · no indexada</p>
            <h1 className="text-3xl sm:text-4xl font-bold mt-2 text-foreground text-balance">Les dades de cotx.es, de l'enunciat a l'SQL</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-3 max-w-2xl text-pretty">
              Treball d'Emmagatzematge de dades fet com els exercicis resolts: enunciat, entitats i atributs, relacions i cardinalitats, model lògic i model físic. Després, la normalització, la càrrega de les dades de debò i les consultes.
            </p>
          </header>

          <nav className="mt-8 pb-4 border-b border-border" aria-label="Passos">
            <ol className="flex flex-wrap gap-1.5">
              {PASSOS.map(([id, label]) => (
                <li key={id} className="shrink-0">
                  <a href={`#${id}`} className="inline-flex h-8 items-center px-3 rounded-full border border-border text-xs text-muted-foreground hover:text-foreground whitespace-nowrap">
                    {label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="mt-10 space-y-16">
            <Seccio id="enunciat" titol="Enunciat">
              <blockquote className="rounded-2xl border border-border bg-secondary/40 p-5 sm:p-6 text-foreground leading-relaxed italic text-pretty space-y-3 max-w-3xl">
                <p>
                  Una web de cotxes vol guardar les <b>marques</b> (nom i descripció) i els seus <b>models</b> a la venda, amb la fitxa tècnica: potència, mides (llargada, amplada i alçada) i maleter. Cada model fa servir un <b>combustible</b>, i cada combustible pertany a una família: elèctric, electrificat o combustió.
                </p>
                <p>
                  Els preus surten d'una revista que publica una <b>edició</b> cada pocs mesos. Cal guardar el preu de cada model a cada edició, per veure com canvien.
                </p>
                <p>
                  També es volen guardar les <b>matrícules</b> que es veuen pel carrer (data, matrícula, marca i model) i els models que les marques ja <b>no fabriquen</b>, amb les generacions i els anys en què es van fer.
                </p>
              </blockquote>
            </Seccio>

            <Seccio
              id="entitats"
              pas="Pas 1"
              titol="Entitats i atributs"
              intro={<p>Els <b className="text-foreground">substantius</b> de l'enunciat són candidats a entitat; el que en diem («nom», «potència») són els atributs. El primer de cada fila és l'identificador.</p>}
            >
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-left">
                    <tr>{["Entitat", "Identificador", "Atributs", "Per què és una entitat"].map((h) => <th key={h} className="px-3 py-2 text-xs font-semibold whitespace-nowrap">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {ENTITATS.map(([e, id, at, why]) => (
                      <tr key={e} className="border-t border-border align-top">
                        <td className="px-3 py-2 font-bold text-xs tracking-wide" style={{ color: "var(--acc)" }}>{e}</td>
                        <td className="px-3 py-2 font-mono text-xs underline underline-offset-2 text-foreground">{id}</td>
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground min-w-[12rem]">{at}</td>
                        <td className="px-3 py-2 text-muted-foreground min-w-[14rem] text-pretty">{why}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4">
                <Clau titol="I la família del combustible? I el preu?">
                  <p><b className="text-foreground">Grupo</b> és un atribut de COMBUSTIBLE, no de MODELO: si canvia la família d'un combustible, ha de canviar per a tots els models alhora.</p>
                  <p><b className="text-foreground">Precio</b> no és de MODELO ni d'EDICION: el mateix model té un preu diferent a cada edició. Va a la relació entre tots dos (pas 2).</p>
                </Clau>
              </div>
            </Seccio>

            <Seccio
              id="relacions"
              pas="Pas 2"
              titol="Relacions i cardinalitats"
              intro={<p>Els <b className="text-foreground">verbs</b> que uneixen dues entitats són les relacions. Per a cada una, la pregunta és: <i>quants B pot tenir un A, i quants A pot tenir un B?</i></p>}
            >
              <ul className="space-y-2 mb-6">
                {RELACIONS.map(([r, d]) => (
                  <li key={r} className="rounded-xl border border-border px-4 py-3">
                    <p className="font-mono text-[13px] sm:text-sm font-semibold text-foreground break-words">{r}</p>
                    <p className="text-sm text-muted-foreground mt-0.5 text-pretty">{d}</p>
                  </li>
                ))}
              </ul>
              <Chen chains={CHEN} alt="marques, models, combustibles, edicions, matriculacions i descatalogats" />
            </Seccio>

            <Seccio
              id="logic"
              pas="Pas 3"
              titol="Model lògic"
              intro={<p>Cada entitat es converteix en una taula. A les relacions <b className="text-foreground">1:N</b>, la clau del costat 1 passa com a <b className="text-foreground">FK</b> al costat N. La relació <b className="text-foreground">N:M</b> es converteix en una taula pont amb PK composta.</p>}
            >
              <Logic />
              <div className="grid gap-3 mt-4 lg:grid-cols-2">
                <Clau titol="Per què MarcaID va a MODELOS?">
                  <p>És el costat N: una marca, molts models. Si anés a MARCAS, una marca hauria de guardar molts ModeloID en una sola cel·la.</p>
                </Clau>
                <Clau titol="Per què hi ha la taula PRECIOS?">
                  <p>MODELO–EDICION és N:M. Amb una FK no n'hi ha prou. La taula pont té per PK la parella (ModeloID, EdicionID) i hi va l'atribut de la relació, el Precio. Resultat: 7 taules a partir de 6 entitats.</p>
                </Clau>
              </div>
            </Seccio>

            <Seccio
              id="fisic"
              pas="Pas 4"
              titol="Model físic (MySQL)"
              intro={<p>Ara cada camp té un tipus i unes restriccions. Tria una taula per veure per què és cada tipus.</p>}
            >
              <Fisic />
              <div className="mt-6 space-y-3">
                <h3 className="font-semibold text-foreground">CREATE TABLE</h3>
                <p className="text-sm text-muted-foreground text-pretty">L'ordre importa: cada taula es crea després de les taules a què apunten les seves FK. Marcas i Combustibles abans que Modelos; Modelos i Ediciones abans que Precios.</p>
                <Sql code={ddl("mysql")} />
                <Baixades />
              </div>
            </Seccio>

            <Seccio id="normalitzacio" pas="Pas 5" titol="Normalització" intro={<p>Comprovació de les tres formes normals amb les dades de debò.</p>}>
              <Normalitzacio />
            </Seccio>

            <Seccio id="carrega" pas="Pas 6" titol="Càrrega de les dades" intro={<p>Les dades que fa servir la web, passades al model relacional.</p>}>
              <Carrega />
            </Seccio>

            <Seccio id="local" pas="Pas 7" titol="A Local (localwp.com)" intro={<p>Com passar la base de dades a la web de WordPress que fa servir Local.</p>}>
              <Local />
            </Seccio>

            <Seccio id="dades" titol="Les dades" intro={<p>Què se'n pot treure. Tot surt de les taules d'aquesta pàgina.</p>}>
              <Panell />
            </Seccio>

            <Seccio id="exercicis" titol="Consultes resoltes" intro={<p>Deu consultes sobre aquestes taules, de la més senzilla a la més complexa, cadascuna amb la solució i el perquè. Totes es poden executar a la consola.</p>}>
              <Exercicis prova={prova} />
            </Seccio>

            <Seccio
              id="consola"
              titol="Consola SQL"
              intro={<p>SQLite al navegador, amb les set taules i les FK actives. Toca una taula per veure-la o escriu la teva consulta; Ctrl+Enter (⌘+Enter) l'executa. El que canviïs no surt d'aquesta pestanya.</p>}
            >
              <Consola query={query} />
            </Seccio>
          </div>
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};
